import {
    Button,
    Heading,
    Pagination,
    TabItem,
    Tabs,
    Text,
} from '@pzh-ui/components'
import { keepPreviousData } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import {
    useModulesGetListModules,
    useObjectsDoListAllLatest,
    useObjectsViewObjectCounts,
} from '@/api/fetchers'
import {
    Module,
    ObjectListAllLatestResponseUnionAmbitieBasicBeleidsdoelBasicBeleidskeuzeBasicBeleidsregelBasicDocumentBasicGebiedsprogrammaBasicMaatregelBasicNationaalBelangBasicGebiedengroepBasicGebiedBasicGebiedsaanwijzingBasicProgrammaAlgemeenBasicVerplichtProgrammaBasicVisieAlgemeenBasicWerkingsgebiedBasicWettelijkeTaakBasic,
} from '@/api/fetchers.schemas'
import ObjectCard from '@/components/DynamicObject/ObjectCard'
import { LoaderCard } from '@/components/Loader'
import ModuleTile from '@/components/Modules/ModuleTile'
import * as models from '@/config/objects'
import { ModelType } from '@/config/objects/types'
import useAuth from '@/hooks/useAuth'

const PAGE_LIMIT = 9

const DashboardUser = () => {
    const { data: modules, isFetching: modulesLoading } =
        useModulesGetListModules(
            {
                filter_activated: true,
                only_mine: false,
                limit: 3,
            },
            {
                query: {
                    placeholderData: keepPreviousData,
                },
            }
        )

    return (
        <div className="col-span-6">
            <div>
                <Heading level="2" size="m" className="mb-4">
                    Modules
                </Heading>

                <div className="mb-4 grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2 xl:grid-cols-3">
                    {modulesLoading ? (
                        <>
                            <LoaderCard height="62" mb="" />
                            <LoaderCard height="62" mb="" />
                            <LoaderCard height="62" mb="" />
                        </>
                    ) : !!modules?.results.length ? (
                        modules?.results?.map(module => (
                            <ModuleTile
                                key={`module-${module.Module_ID}`}
                                {...module}
                            />
                        ))
                    ) : (
                        <Text>Er zijn op dit moment geen actieve modules.</Text>
                    )}
                </div>

                <Button asChild variant="secondary" size="small">
                    <Link to="/muteer/modules">Bekijk alle modules</Link>
                </Button>

                <div className="mt-8 grid grid-cols-6">
                    <div className="col-span-6 mb-6 lg:col-span-3">
                        <Heading level="3" size="m" className="mb-4">
                            Mijn beleid
                        </Heading>
                        <Text>
                            Binnen het digitaal omgevingsbeleid ben jij eigenaar
                            van een aantal beleidsobjecten, hieronder vind je
                            een overzicht van deze onderdelen.
                        </Text>
                    </div>

                    <UserObject />
                </div>
            </div>
        </div>
    )
}

const UserObject = () => {
    const { user } = useAuth()

    const [activeTab, setActiveTab] = useState<ModelType>()
    const [currPage, setCurrPage] = useState(1)

    const { data: availableObjectTypes } = useObjectsViewObjectCounts()

    const { data: objects, isFetching } = useObjectsDoListAllLatest(
        {
            owner_uuid: user?.UUID,
            object_types: activeTab ? [activeTab] : [],
            limit: PAGE_LIMIT,
            offset: (currPage - 1) * PAGE_LIMIT,
        },
        {
            query: {
                enabled: !!user?.UUID && !!activeTab,
                placeholderData: keepPreviousData,
            },
        }
    )

    return (
        <>
            {!!availableObjectTypes?.length ? (
                <div className="col-span-6">
                    <Tabs
                        selectedKey={activeTab}
                        onSelectionChange={key => {
                            setActiveTab(key as ModelType)
                            setCurrPage(1)
                        }}>
                        {availableObjectTypes?.map(type => {
                            const model = models[type.object_type as ModelType]

                            return (
                                <TabItem
                                    key={type.object_type}
                                    title={`${model.defaults.pluralCapitalize} (${type.count})`}>
                                    <ItemList
                                        items={objects?.results}
                                        isLoading={isFetching}
                                        type="object"
                                    />
                                    {!!objects?.total &&
                                        !!objects?.limit &&
                                        objects.total > objects.limit && (
                                            <div className="mt-8 flex justify-center">
                                                <Pagination
                                                    onPageChange={setCurrPage}
                                                    current={currPage}
                                                    total={objects.total}
                                                    limit={objects.limit}
                                                />
                                            </div>
                                        )}
                                </TabItem>
                            )
                        })}
                    </Tabs>
                </div>
            ) : (
                <div className="col-span-6">
                    <span className="italic">
                        Geen beleidsobjecten gevonden
                    </span>
                </div>
            )}
        </>
    )
}

interface ItemListProps {
    isLoading: boolean
    items?:
        | Module[]
        | ObjectListAllLatestResponseUnionAmbitieBasicBeleidsdoelBasicBeleidskeuzeBasicBeleidsregelBasicDocumentBasicGebiedsprogrammaBasicMaatregelBasicNationaalBelangBasicGebiedengroepBasicGebiedBasicGebiedsaanwijzingBasicProgrammaAlgemeenBasicVerplichtProgrammaBasicVisieAlgemeenBasicWerkingsgebiedBasicWettelijkeTaakBasic[]
    type: 'module' | 'object'
}

const ItemList = ({ isLoading, items, type }: ItemListProps) => (
    <>
        {isLoading ? (
            <div className="mt-4">
                <LoaderCard height="62" mb="" />
                <LoaderCard height="62" mb="" />
                <LoaderCard height="62" mb="" />
            </div>
        ) : (
            <>
                {items?.length ? (
                    <ul className="mt-4">
                        {items.map(item =>
                            'Module_ID' in item &&
                            'Status' in item &&
                            type === 'module' ? (
                                <ModuleTile key={item.Module_ID} {...item} />
                            ) : (
                                'Model' in item &&
                                type === 'object' && (
                                    <ObjectCard
                                        key={item.Model.UUID}
                                        {...item}
                                    />
                                )
                            )
                        )}
                    </ul>
                ) : (
                    <span className="mt-3 block italic">
                        Geen modules gevonden.
                    </span>
                )}
            </>
        )}
    </>
)

export default DashboardUser
